import { createContext, useContext, useEffect, useState } from 'react'
import posthog from 'posthog-js'
import type { PropsWithChildren } from 'react'

import { useAuthenticator } from '@aws-amplify/ui-react'
import type { platformKeysEnum } from '@motrix/core'
import { Loader } from '@motrix/core'

import { getWorkspaceFromCookie } from './workspaces/cookie-store'
import { PermissionsProvider } from './permissions'

type AuthenticationState = {
  acceptedTermsOfUse: boolean
  accessToken: string
  availablePlatforms: Array<{
    platform: string
    url: string
    label: string
  }>
  currentWorkspace: {
    id: string
    label: string
    name: string
    actions: Array<string>
    config?: {
      curricula?: {
        showAiDescription: false
        showTopics: false
      }
    }
  }
  email: string
  familyName: string
  givenName: string
  id: string
  idToken: string
  loggedIn: boolean
  memberships: Array<{
    id: string
    workspace: {
      id: string
      label: string
      name: string
    }
    roles: Array<{
      id: string
      label: string
      value: string
      actions: Array<string>
    }>
  }>
  pictureUrl: string
  username: string
}

const initialState: AuthenticationState = {
  acceptedTermsOfUse: false,
  accessToken: '',
  availablePlatforms: [],
  currentWorkspace: {} as AuthenticationState['currentWorkspace'],
  email: '',
  familyName: '',
  givenName: '',
  id: '',
  idToken: '',
  loggedIn: false,
  memberships: [],
  pictureUrl: '',
  username: '',
}

export const AuthenticationContext =
  createContext<AuthenticationState>(initialState)

export const useAuthentication = () => {
  const me = useContext(AuthenticationContext)
  const authenticator = useAuthenticator()
  const hasAccess = (platformKey: platformKeysEnum) => {
    return me.availablePlatforms.some(
      ({ platform }) => platform === platformKey,
    )
  }

  const redirectTo403 = () => {
    const currentLocation = window.location.pathname
    if (currentLocation === '/403') {
      return
    }
    window.location.href = '/403'
  }
  return {
    ...authenticator,
    hasAccess,
    me,
    redirectTo403,
  }
}

export const workspaceIdHeaderName = 'x-workspace-id'

export const Provider = ({ children }: PropsWithChildren) => {
  const { user, signOut } = useAuthenticator()
  const [state, setState] = useState(initialState)

  useEffect(() => {
    const cb = async () => {
      const accessToken =
        user.getSignInUserSession()?.getAccessToken().getJwtToken() ?? ''
      const idToken =
        user.getSignInUserSession()?.getIdToken().getJwtToken() ?? ''

      const headers = new Headers()
      headers.set('Authorization', `Bearer ${accessToken}`)

      const workspaceId = getWorkspaceFromCookie()
      if (workspaceId) {
        headers.append(workspaceIdHeaderName, workspaceId)
      }

      const request = await fetch(`${process.env.API_URL}/me`, {
        headers,
      })

      if (request.status === 401) {
        signOut()
        return
      }

      const response = await request.json()

      const email = user.attributes?.email ?? ''
      setState({
        acceptedTermsOfUse: response.acceptedTermsOfUse,
        accessToken,
        availablePlatforms: response.availablePlatforms,
        currentWorkspace: response.currentWorkspace,
        email,
        familyName: user.attributes?.['family_name'] ?? '',
        givenName: user.attributes?.['given_name'] ?? '',
        id: response.id,
        idToken,
        loggedIn: true,
        memberships: response.memberships,
        pictureUrl: response.pictureUrl,
        username: user.username ?? '',
      })
      const name = `${user.attributes?.['given_name']} ${user.attributes?.['family_name']}`
      posthog.identify(response.id, { email, name })
    }

    cb()
  }, [user])

  return (
    <AuthenticationContext.Provider value={state}>
      {!state.loggedIn && <Loader />}
      {state.loggedIn && <PermissionsProvider>{children}</PermissionsProvider>}
    </AuthenticationContext.Provider>
  )
}
